import { useMemo } from "react"
import { useCubeAvailability, useCubeQuery } from "~/ui-rtk/hooks/cube"
import useDate from "~/ui-rtk/hooks/date"

const dayjs = useDate()

export const AI_MENTIONS_CUBE = "company_ai_mentions_metrics"
export const AI_MENTIONS_DATE_DIMENSION = `${AI_MENTIONS_CUBE}.md_date`

export type TAnalyzedMentionsCoverage = {
  /** Capabilities or the latest-date lookup are still resolving. */
  isLoading: boolean
  /** False once it is known that the brand has no analyzed mentions at all. */
  hasAnalyzedMentions: boolean
  /** Day of the most recent analyzed mention, once resolved. */
  latestDate?: Date
}

const LATEST_DATE_QUERY = {
  dimensions: [AI_MENTIONS_DATE_DIMENSION],
  order: { [AI_MENTIONS_DATE_DIMENSION]: "desc" as const },
  limit: 1,
}

/**
 * Where the AI-analyzed mentions behind the Mentions Analysis page end.
 *
 * The analysis is produced by a weekly batch job that can trail the raw
 * mentions feed by months, or never run for a brand at all. Anchoring the page
 * to the company-wide max date therefore opens it on a window that lies after
 * every analyzed mention, and each widget renders empty. This hook resolves
 * the latest analyzed day so the page can anchor its date range to it and say
 * plainly when there is nothing to show.
 *
 * Not built on `useLastUpdateDate`: that hook is written for the one-row
 * company_last_updated_date cube and deliberately returns the second row of a
 * two-row result, which for a many-day dimension query is the wrong day.
 */
export function useAnalyzedMentionsCoverage(): TAnalyzedMentionsCoverage {
  const { isLoaded, isAvailable } = useCubeAvailability(AI_MENTIONS_CUBE)
  const { resultSet, error } = useCubeQuery(LATEST_DATE_QUERY, {
    skip: !isAvailable,
  })

  const rawLatest = resultSet?.rawData()?.[0]?.[AI_MENTIONS_DATE_DIMENSION]
  const latestDate = useMemo(
    () =>
      rawLatest ? dayjs(String(rawLatest)).startOf("day").toDate() : undefined,
    [rawLatest],
  )

  if (!isLoaded) {
    return { isLoading: true, hasAnalyzedMentions: true }
  }
  // No mentions_unnested table for this company: nothing has been analyzed.
  if (!isAvailable) {
    return { isLoading: false, hasAnalyzedMentions: false }
  }
  // Fall through to the widgets, which surface query failures themselves.
  if (error) {
    return { isLoading: false, hasAnalyzedMentions: true }
  }
  // A missing result set is the only unresolved state. The client's
  // `isLoading` flag is deliberately not consulted: a revalidation (every Cube
  // API client swap after a token refresh) sets it while keeping the previous
  // result set, so the page keeps its previous latest date instead of being
  // torn down and remounted.
  if (!resultSet) {
    return { isLoading: true, hasAnalyzedMentions: true }
  }
  if (!latestDate) {
    return { isLoading: false, hasAnalyzedMentions: false }
  }
  return { isLoading: false, hasAnalyzedMentions: true, latestDate }
}
